// app/src/contexts/AuthContext.tsx
import axios, { AxiosResponse } from 'axios';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useDevSecretShortcut from '../hooks/useDevSecretShortcut';
import CompaniesService from '../services/Companies/CompaniesService';
import JWTLegacyService from '../services/JWTLegacyService';
import PartsService from '../services/Parts/PartsService';
import ProfessionalsService from '../services/Professionals/ProfessionalsService';
import VPAutoService from '../services/VPAuto/VPAutoService';
import {
  AuthContextProps,
  CompanyLookupTelemetry,
  CountryLabels,
  ExistingCompanyMatch,
  UserInfo,
  UserType,
} from '../types';
import allCountriesList from '../utils/constants/allCountriesList';
import toastConfig from '../utils/constants/toastConfig';
import isValidEmail from '../utils/functions/isValidEmail';
import setFirstAndThirdBits from '../utils/functions/setFirstAndThirdBits';
import { useUrlContext } from './UrlContext';
import { emitAnalytics, getTelemetryDeviceType } from '../services/telemetry/appInsights';

const AuthContext = createContext<AuthContextProps | undefined>(undefined);
  type UpdateFunction = (id: string, data: {
    fileBase64: string;
    contentType: string;
    documentType?: 'recto' | 'verso';
  }) => Promise<any>;

const STORAGE_KEY = 'signupUserInfo';

/**
 * Statuts renvoyés par ProfessionalsService.proOrPartReadByEmail(email)
 */
type CpStatus =
  | 'Unregistered user'
  | 'Unassigned user'
  | 'Professional user'
  | 'Particular Company user'
  | 'Particular Customer user';

type LegacyInfo = { result: boolean; adId: string | null };
const isProCp = (s: CpStatus) => s === 'Professional user';
const isPartCp = (s: CpStatus) =>
  s === 'Particular Company user' || s === 'Particular Customer user';

type LegacyLookup =
  | { kind: 'FOUND'; data: LegacyInfo }
  | { kind: 'NOT_FOUND' }
  | { kind: 'ERROR'; error: unknown };

type LegacyAction =
  | { type: 'NONE' }
  | { type: 'PART_SSO_LOGIN' }
  | { type: 'CREATE_AND_MIGRATE_UI' }
  | { type: 'RESET_PASSWORD_AND_FORCE_MIGRATE' };

type AuthMode = 'part' | 'pro';

const getCountryCodeFromLanguage = (language: string): string => {
  const languageToCountry: {
    [key: string]: string
  } = {
    fr: 'FR', en: 'GB', de: 'DE', es: 'ES', it: 'IT', nl: 'NL', pl: 'PL', pt: 'PT', ro: 'RO'
  };

  return languageToCountry[language] || 'FR';
};

const getLanguageForApi = (language: string): string => {
  const countryCode = getCountryCodeFromLanguage(language);
  return countryCode === 'GB' ? 'EN' : countryCode;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const {
    redirectToProAfterSignup,
    redirectToPartAfterSignup,
    redirectToMicrosoftAuth,
  } = useUrlContext();

  const currentLanguage = i18n.language;
  const navigate = useNavigate();

  const [loadingSignin, setLoadingSignin] = useState(false);
  const [loadingSignup, setLoadingSignup] = useState(false);
  const signupSubmissionInFlightRef = useRef(false);
  const [callbackUrl, setCallbackUrl] = useState('');
  const [userInfo, setUserInfo] = useState<UserInfo>(null as any);
  const [openedPanel, setOpenedPanel] = useState<string>('coordinates and contact');
  const [isFirstPanelValidated, setIsFirstPanelValidated] = useState<boolean>(false);
  const [isSecondPanelValidated, setIsSecondPanelValidated] = useState<boolean>(false);
  const [isThirdPanelValidated, setIsThirdPanelValidated] = useState<boolean>(false);
  const [existingCompanyMatch, setExistingCompanyMatch] = useState<ExistingCompanyMatch | null>(null);
  const shouldSkipDocumentUploadStep = existingCompanyMatch !== null;

  // Add secret key combination handler to fast signup
  useDevSecretShortcut(setUserInfo);

  const buildTelemetryBase = useCallback(
    () => ({
      deviceType: getTelemetryDeviceType(),
      language: i18n.language,
    }),
    [i18n.language]
  );

  // ---------------------------------------------------------------------------
  // LocalStorage
  // ---------------------------------------------------------------------------
  const loadUserInfoFromLocalStorage = useCallback(
    (email: string) => {
      const storedData = localStorage.getItem(STORAGE_KEY);

      if (storedData) {
        try {
          const parsedData: UserInfo = JSON.parse(storedData);

          if (parsedData?.contact?.email === email) {
            if (!parsedData.company.countryCode) {
              parsedData.company.countryCode = getCountryCodeFromLanguage(i18n.language);
            }
            setUserInfo(parsedData);
          } else if (email.length === 0) {
            if (!parsedData.company.countryCode) {
              parsedData.company.countryCode = getCountryCodeFromLanguage(i18n.language);
            }
            setUserInfo(parsedData);
          } else {
            localStorage.removeItem(STORAGE_KEY);
            loadUserInfoFromLocalStorage(email);
          }
        } catch (error) {
          console.error("Erreur lors de l'analyse du localStorage:", error);
        }
        return;
      }

      if (email.length > 0) {
        setUserInfo({
          contact: {
            profile: '',
            email,
            firstName: '',
            lastName: '',
            phone: '',
            function: '',
            howDidYouKnowUs: '',
          },
          company: {
            countryCode: getCountryCodeFromLanguage(i18n.language),
            companyName: '',
            nbtva: '',
            siret: '',
            address: '',
            postalCode: '',
            siren: '',
            city: '',
          },
          receiveRecommendation: { email: false, sms: false },
          documents: { companyRegistration: [], identityCard: [], identityCardBack: [], other: [] },
        });
      }
    },
    [i18n.language]
  );

  // ---------------------------------------------------------------------------
  // Legacy lookup + décision + exécution
  // ---------------------------------------------------------------------------
  const getLegacyLookup = useCallback(
    async (email: string): Promise<LegacyLookup> => {
      try {
        const res: AxiosResponse<LegacyInfo> = await VPAutoService.isUserLegacy(email, {
          // Protect against slow 404 (timeout should not be treated as NOT_FOUND)
          timeout: 8000,
        });

        return { kind: 'FOUND', data: res.data };
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          // True NOT_FOUND only
          if (Number(error.response?.status) === 404) {
            return { kind: 'NOT_FOUND' };
          }
          // Timeout / network / unknown => ERROR (avoid false NOT_FOUND)
          return { kind: 'ERROR', error };
        }

        return { kind: 'ERROR', error };
      }
    },
    []
  );

  const decideLegacyAction = useCallback(
    (legacy: LegacyInfo, cpStatus?: CpStatus): LegacyAction => {
      if (!legacy.result) return { type: 'NONE' };

      // VPauto "legacy" mais adId null,mais user existe dans CP (PartCompany/PartCustomer)
      // => on force la connexion (SSO B2C)
      if (!legacy.adId && cpStatus && isPartCp(cpStatus)) {
        return { type: 'PART_SSO_LOGIN' };
      }

      // legacy.result === true
      if (legacy.adId) {
        // adId présent
        if (cpStatus === 'Unregistered user') {
          return { type: 'CREATE_AND_MIGRATE_UI' };
        }
        return { type: 'RESET_PASSWORD_AND_FORCE_MIGRATE' };
      }

      // legacy sans adId => create
      return { type: 'CREATE_AND_MIGRATE_UI' };
    },
    []
  );

  const runLegacyAction = useCallback(
    async (action: LegacyAction, email: string) => {
      switch (action.type) {
        case 'NONE':
          return false;

        case 'PART_SSO_LOGIN':
          emitAnalytics(`part_login_redirected_to_ms`, {
            entryRoute: `/part`,
            ...buildTelemetryBase(),
          });
          redirectToMicrosoftAuth('b2c', email);
          return true;

        case 'CREATE_AND_MIGRATE_UI':
          try {
            emitAnalytics('part_migration_legacy_create_started', {
              action: 'legacy_create',
              cpStatus: 'Unregistered user',
              hasAdId: false,
              ...buildTelemetryBase(),
            });
            await VPAutoService.partLegacyCreate({
              email,
              language: getLanguageForApi(i18n.language),
            });
            emitAnalytics('part_migration_legacy_create_succeeded', {
              action: 'legacy_create',
              cpStatus: 'Unregistered user',
              hasAdId: false,
              ...buildTelemetryBase(),
            });
            navigate('/part/legacyMigrate');
            return true;
          } catch (error) {
            emitAnalytics('part_migration_legacy_create_failed', {
              action: 'legacy_create',
              cpStatus: 'Unregistered user',
              failureStep: 'legacy_create',
              ...buildTelemetryBase(),
            });
            throw error;
          }

        case 'RESET_PASSWORD_AND_FORCE_MIGRATE':
          try {
            emitAnalytics('part_migration_legacy_reset_password_started', {
              action: 'reset_password',
              cpStatus: 'Unregistered user',
              hasAdId: true,
              ...buildTelemetryBase(),
            });
            await PartsService.passwordReset(email);
            await VPAutoService.partLegacyMigrate({ email });
            emitAnalytics('part_migration_legacy_reset_password_succeeded', {
              action: 'reset_password',
              cpStatus: 'Unregistered user',
              hasAdId: true,
              ...buildTelemetryBase(),
            });
            navigate('/part/legacyResetPassword');
            return true;
          } catch (error) {
            emitAnalytics('part_migration_legacy_reset_password_failed', {
              action: 'reset_password',
              cpStatus: 'Unregistered user',
              failureStep: 'legacy_reset_password',
              ...buildTelemetryBase(),
            });
            throw error;
          }
      }
    },
    [buildTelemetryBase, i18n.language, navigate, redirectToMicrosoftAuth]
  );

  // ---------------------------------------------------------------------------
  // Orchestrateur unique
  // ---------------------------------------------------------------------------
  const authenticateUser = useCallback(
    async (email: string, mode: AuthMode) => {
      emitAnalytics(`${mode}_login_attempted`, {
        entryRoute: `/${mode}`,
        ...buildTelemetryBase(),
      });

      const cpStatus = (await ProfessionalsService.proOrPartReadByEmail(email))
        .data as CpStatus;

      loadUserInfoFromLocalStorage(email);

      // CP bloque
      if (cpStatus === 'Unassigned user') {
        toast.error(t('error:unassigned_user'), toastConfig);
        return;
      }

      // CP pro => B2B direct
      if (isProCp(cpStatus)) {
        emitAnalytics(`${mode}_login_redirected_to_ms`, {
          entryRoute: `/${mode}`,
          ...buildTelemetryBase(),
        });
        redirectToMicrosoftAuth('b2b', email);
        return;
      }

      // Ici: cpStatus part ou unregistered
      emitAnalytics('part_migration_entry_attempted', {
        entryRoute: '/part',
        ...buildTelemetryBase(),
      });
      emitAnalytics('part_migration_legacy_lookup_started', {
        entryRoute: '/part',
        ...buildTelemetryBase(),
      });
      const legacyLookup = await getLegacyLookup(email);

      if (legacyLookup.kind === 'ERROR') {
        emitAnalytics('part_migration_legacy_lookup_result', {
          legacyResult: 'error',
          cpStatus,
          hasAdId: false,
          ...buildTelemetryBase(),
        });
        console.error(legacyLookup.error);
        throw legacyLookup.error;
      }

      emitAnalytics('part_migration_legacy_lookup_result', {
        legacyResult: legacyLookup.kind === 'FOUND' ? 'found' : 'not_found',
        cpStatus,
        hasAdId: legacyLookup.kind === 'FOUND' ? Boolean(legacyLookup.data.adId) : false,
        ...buildTelemetryBase(),
      });

      // N'existe pas dans VPauto
      if (legacyLookup.kind === 'NOT_FOUND') {
        // Si l'utilisateur existe dans CP (donc pas "Unregistered user") => SSO
        if (cpStatus !== 'Unregistered user') {
          emitAnalytics('part_migration_action_decided', {
            action: 'sso_b2c',
            cpStatus,
            legacyResult: 'not_found',
            hasAdId: false,
            ...buildTelemetryBase(),
          });
          emitAnalytics('part_migration_sso_redirected', {
            action: 'sso_b2c',
            cpStatus,
            legacyResult: 'not_found',
            ...buildTelemetryBase(),
          });
          emitAnalytics(`${mode}_login_redirected_to_ms`, {
            entryRoute: `/${mode}`,
            ...buildTelemetryBase(),
          });
          // Ici cpStatus est forcément un Part (Company/Customer) puisque "Professional user" est géré plus haut
          redirectToMicrosoftAuth('b2c', email);
          return;
        }

        // N'existe ni dans VPauto ni dans CP => signup selon le mode
        if (mode === 'part') {
          emitAnalytics('part_migration_action_decided', {
            action: 'signup_redirect',
            cpStatus,
            legacyResult: 'not_found',
            hasAdId: false,
            ...buildTelemetryBase(),
          });
          emitAnalytics('part_migration_signup_redirected', {
            action: 'signup_redirect',
            cpStatus,
            legacyResult: 'not_found',
            ...buildTelemetryBase(),
          });
          navigate('/part/signup');
          return;
        }

        setOpenedPanel('coordinates and contact');
        navigate('/pro/signup');
        return;
      }

      // Existe dans VPauto: legacy ou non-legacy
      const action = decideLegacyAction(legacyLookup.data, cpStatus);
      emitAnalytics('part_migration_action_decided', {
        action:
          action.type === 'PART_SSO_LOGIN'
            ? 'sso_b2c'
            : action.type === 'CREATE_AND_MIGRATE_UI'
              ? 'legacy_create'
              : action.type === 'RESET_PASSWORD_AND_FORCE_MIGRATE'
                ? 'reset_password'
                : 'none',
        cpStatus,
        legacyResult: 'found',
        hasAdId: Boolean(legacyLookup.data.adId),
        ...buildTelemetryBase(),
      });
      if (await runLegacyAction(action, email)) return;

      // Non-legacy => SSO B2C
      // TODO : Protect here against slow 404
      emitAnalytics(`${mode}_login_redirected_to_ms`, {
        entryRoute: `/${mode}`,
        ...buildTelemetryBase(),
      });
      redirectToMicrosoftAuth('b2c', email);
    },
    [
      buildTelemetryBase,
      decideLegacyAction,
      getLegacyLookup,
      loadUserInfoFromLocalStorage,
      navigate,
      redirectToMicrosoftAuth,
      runLegacyAction,
      setOpenedPanel,
      t,
    ]
  );

  // ---------------------------------------------------------------------------
  // Public API: handleUserAuthentication
  // ---------------------------------------------------------------------------
  const handleUserAuthentication = useCallback(
    async (email: string, userType: UserType) => {
      if (!isValidEmail(email)) {
        toast.warn(t('homePage:email_invalid'), toastConfig);
        return;
      }

      setLoadingSignin(true);
      try {
        const mode: AuthMode = userType === UserType.PART ? 'part' : 'pro';
        await authenticateUser(email, mode);
      } catch (error: unknown) {
        console.error(error);
      } finally {
        setLoadingSignin(false);
      }
    },
    [authenticateUser, t]
  );

  // ---------------------------------------------------------------------------
  // Persist userInfo
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!userInfo?.contact?.email) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(userInfo));
  }, [userInfo]);

  // ---------------------------------------------------------------------------
  // Company lookup helpers
  // ---------------------------------------------------------------------------
  const clearUploadedDocuments = useCallback(() => {
    setUserInfo((prevState) => ({
      ...prevState,
      documents: { companyRegistration: [], identityCard: [], identityCardBack: [], other: [] },
    }));
  }, []);

  const findExistingCompanyByVAT = useCallback(
    async (vatNumber?: string): Promise<
      | { kind: 'MATCH'; match: ExistingCompanyMatch }
      | { kind: 'NO_MATCH' }
      | { kind: 'ERROR'; error: unknown }
    > => {
      const cleanVatNumber = vatNumber?.trim();
      if (!cleanVatNumber) return { kind: 'NO_MATCH' };

      try {
        const response = await CompaniesService.companyReadByNBTVA(cleanVatNumber);
        if (!response.data?.id) return { kind: 'NO_MATCH' };

        return {
          kind: 'MATCH',
          match: {
            companyID: response.data.id,
            roles: response.data.roles ?? 0,
          },
        };
      } catch (error: unknown) {
        if (axios.isAxiosError(error) && Number(error.response?.status) === 404) {
          return { kind: 'NO_MATCH' };
        }

        console.error('Erreur lors de la recherche de société par TVA:', error);
        return { kind: 'ERROR', error };
      }
    },
    []
  );

  const syncExistingCompanyMatchByVAT = useCallback(
    async (vatNumber?: string, telemetry?: Partial<CompanyLookupTelemetry>) => {
      const cleanVatNumber = vatNumber?.trim();
      if (!cleanVatNumber) {
        setExistingCompanyMatch(null);
        return null;
      }

      emitAnalytics('pro_signup_company_lookup_started', {
        countryCode: telemetry?.countryCode ?? userInfo?.company?.countryCode,
        identifierType: 'vat',
        lookupMode: telemetry?.lookupMode ?? 'classic',
        ...buildTelemetryBase(),
      });

      const lookupResult = await findExistingCompanyByVAT(cleanVatNumber);

      if (lookupResult.kind === 'MATCH') {
        setExistingCompanyMatch(lookupResult.match);
        emitAnalytics('pro_signup_company_lookup_result', {
          countryCode: telemetry?.countryCode ?? userInfo?.company?.countryCode,
          identifierType: 'vat',
          lookupMode: telemetry?.lookupMode ?? 'classic',
          lookupOutcome: 'match',
          ...buildTelemetryBase(),
        });
        return lookupResult.match;
      }

      setExistingCompanyMatch(null);
      emitAnalytics('pro_signup_company_lookup_result', {
        countryCode: telemetry?.countryCode ?? userInfo?.company?.countryCode,
        identifierType: 'vat',
        lookupMode: telemetry?.lookupMode ?? 'classic',
        lookupOutcome: lookupResult.kind === 'NO_MATCH' ? 'no_match' : 'error',
        ...buildTelemetryBase(),
      });
      return null;
    },
    [buildTelemetryBase, findExistingCompanyByVAT, userInfo?.company?.countryCode]
  );

  const activateExistingCompanyAttachment = useCallback(
    (match: ExistingCompanyMatch) => {
      /*
       * Ancien flux rattachement conservé pour réintégration.
       * Il ouvrait le troisième panneau en mode "company_rattachement" et
       * préparait ensuite la création du contact rattaché.
       */

      emitAnalytics('pro_signup_company_rattachement_started', {
        countryCode: userInfo?.company?.countryCode,
        lookupMode: userInfo?.company?.countryCode === 'FR' ? 'reduced' : 'classic',
        lookupOutcome: 'match',
        flowType: 'company_rattachement',
        autoOpened: true,
        ...buildTelemetryBase(),
      });

      setExistingCompanyMatch(match);
      setIsSecondPanelValidated(true);
      setIsThirdPanelValidated(false);
      setOpenedPanel('documents');
      clearUploadedDocuments();
    },
    [buildTelemetryBase, clearUploadedDocuments, userInfo?.company?.countryCode]
  );

  useEffect(() => {
    setExistingCompanyMatch(null);
  }, [userInfo?.company?.nbtva, userInfo?.company?.siret, userInfo?.company?.countryCode]);
  // ---------------------------------------------------------------------------
  // Document helper
  // ---------------------------------------------------------------------------
  const processAndUpdateDocument = async (
    id: string,
    dataUrl: string,
    updateFunction: UpdateFunction,
    extraData: {
      documentType?: 'recto' | 'verso';
    } = {}
  ) => {
    try {
      const [header, base64Data] = dataUrl.split(',');
      const contentTypeMatch = header.match(/data:(.*);base64/);
      if (!contentTypeMatch) {
        throw new Error('Format de data URL invalide.');
      }
      const contentType = contentTypeMatch[1];

      const dataToSend = { fileBase64: base64Data, contentType, ...extraData };
      await updateFunction(id, dataToSend);
    } catch (error) {
      console.error('Erreur lors du traitement du document:', error);
      throw error;
    }
  };

  // ---------------------------------------------------------------------------
  // Signup PRO
  // ---------------------------------------------------------------------------
  const submitProSignup = useCallback(async (existingCompanyMatchOverride?: ExistingCompanyMatch | null) => {
    const resolvedExistingCompanyMatch = existingCompanyMatchOverride ?? existingCompanyMatch;
    if (resolvedExistingCompanyMatch) {
      /*
       * Dead-end temporaire: on ne crée rien côté backend lorsqu'une société
       * existe déjà. Le flux historique de rattachement est conservé ici pour
       * une réintégration ultérieure.
       */
      return;
    }

    let companyID: string | undefined;
    let roles = 0;
    let submissionFailed = false;

    emitAnalytics('pro_signup_submission_started', {
      flowType: resolvedExistingCompanyMatch ? 'company_rattachement' : 'classic',
      skipDocuments: Boolean(resolvedExistingCompanyMatch),
      ...buildTelemetryBase(),
    });

    setLoadingSignup(true);

    if (!companyID) {
      const address = userInfo?.company.address;
      const streetNumberMatch = address.match(/\d+/);
      const streetNumber = streetNumberMatch ? streetNumberMatch[0] : '';
      const streetName = streetNumber ? address.replace(streetNumber, '').trim() : address;

      const companyLocation = {
        streetNumber: userInfo?.company.countryCode === 'FR' ? streetNumber : '',
        streetName:
          userInfo?.company.countryCode === 'FR'
            ? streetName
            : userInfo?.company.address,
        localName: userInfo?.company.city,
        postalCode: userInfo?.company.postalCode,
        countryCode: userInfo?.company.countryCode,
        country:
          allCountriesList.find((country) => country.countryCode === userInfo?.company.countryCode)
            ?.label[currentLanguage.toLowerCase() as keyof CountryLabels] || '',
      };

      const company = {
        name: userInfo?.company.companyName,
        language: getLanguageForApi(i18n.language),
        siret: userInfo?.company.siret,
        siren: userInfo?.company.siren,
        roles: 5,
        location: [companyLocation],
        invoicingLocation: [companyLocation],
        deliveryLocation: [companyLocation],
        isCarProfessional: true,
        vat: userInfo?.company.nbtva,
      };

      try {
        const response = await CompaniesService.companyCreate(company);
        companyID = response.data.id;
      } catch (err: unknown) {
        setLoadingSignup(false);
        submissionFailed = true;
        emitAnalytics('pro_signup_submission_failed', {
          flowType: resolvedExistingCompanyMatch ? 'company_rattachement' : 'classic',
          skipDocuments: Boolean(resolvedExistingCompanyMatch),
          failureStep: 'company_create',
          ...buildTelemetryBase(),
        });

        if (axios.isAxiosError(err)) {
          const data =
            (err.response?.data as { error?: string; details?: string[] } | undefined) ?? undefined;

          if (data?.details?.length) {
            data.details.forEach((msg) => toast.error(msg, toastConfig));
            return;
          }

          if (data?.error) {
            toast.error(data.error, toastConfig);
            return;
          }
        }

        toast.error(t('common:unexpected_error'), toastConfig);
        console.error('companyCreateError', err);
        return;
      }
    }

    if (companyID) {

      if (userInfo?.documents.identityCard.length > 0) {
        const dataUrl = userInfo.documents.identityCard[0].data;
        try {
          await processAndUpdateDocument(
            companyID,
            dataUrl,
            ProfessionalsService.professionalUpdateDocument,
            { documentType: 'recto' }
          );
        } catch (error) {
          console.error("Erreur lors de la mise à jour du recto de la pièce d'identité:", error);
        }
      }

      if (userInfo?.documents.identityCardBack.length > 0) {
        const dataUrl = userInfo.documents.identityCardBack[0].data;
        try {
          await processAndUpdateDocument(
            companyID,
            dataUrl,
            ProfessionalsService.professionalUpdateDocument,
            { documentType: 'verso' }
          );
        } catch (error) {
          console.error("Erreur lors de la mise à jour du verso de la pièce d'identité:", error);
        }
      }

      if (userInfo?.documents.companyRegistration.length > 0) {
        const dataUrl = userInfo.documents.companyRegistration[0].data;
        try {
          await processAndUpdateDocument(companyID, dataUrl, CompaniesService.companyUpdateKbis);
        } catch (error) {
          console.error('Erreur lors de la mise à jour du Kbis:', error);
        }
      }

    }

    let professionalID: string | undefined;

    if (companyID) {
      const professional = {
        firstName: userInfo?.contact.firstName,
        lastName: userInfo?.contact.lastName,
        phoneNumber: userInfo?.contact.phone,
        email: userInfo?.contact.email.toLowerCase().trim(),
        assignedEntityId: companyID,
        sendWelcomeEmail: true,
        preferedLanguage: getLanguageForApi(i18n.language),
        roles: setFirstAndThirdBits(roles),
        functions: [userInfo?.contact.function],
        referralSource: userInfo?.contact.howDidYouKnowUs,
        mailConsent: true,
        smsConsent: true,
        status: false,
      };

      try {
        const response = await ProfessionalsService.professionalCreate(professional);
        professionalID = response.data.id;
      } catch (error) {
        submissionFailed = true;
        emitAnalytics('pro_signup_submission_failed', {
          flowType: resolvedExistingCompanyMatch ? 'company_rattachement' : 'classic',
          skipDocuments: Boolean(resolvedExistingCompanyMatch),
          failureStep: 'final_submit',
          ...buildTelemetryBase(),
        });
        console.error(error);
      }
    }

    if (professionalID) {
      emitAnalytics('pro_signup_submission_succeeded', {
        flowType: resolvedExistingCompanyMatch ? 'company_rattachement' : 'classic',
        skipDocuments: Boolean(resolvedExistingCompanyMatch),
        ...buildTelemetryBase(),
      });
      setLoadingSignup(false);
      localStorage.removeItem(STORAGE_KEY);
      redirectToProAfterSignup();
      return;
    }

    if (!submissionFailed) {
      emitAnalytics('pro_signup_submission_failed', {
        flowType: resolvedExistingCompanyMatch ? 'company_rattachement' : 'classic',
        skipDocuments: Boolean(resolvedExistingCompanyMatch),
        failureStep: 'final_submit',
        ...buildTelemetryBase(),
      });
    }
    setLoadingSignup(false);
  }, [buildTelemetryBase, currentLanguage, existingCompanyMatch, i18n.language, redirectToProAfterSignup, t, userInfo]);

  const handleSignUpPro = useCallback(async () => {
    if (signupSubmissionInFlightRef.current) {
      return;
    }

    signupSubmissionInFlightRef.current = true;
    setLoadingSignup(true);

    try {
      await submitProSignup();
    } finally {
      signupSubmissionInFlightRef.current = false;
      setLoadingSignup(false);
    }
  }, [submitProSignup]);

  // ---------------------------------------------------------------------------
  // Signup PART
  // ---------------------------------------------------------------------------
  const handleSignUpPart = useCallback(async () => {
    if (signupSubmissionInFlightRef.current) {
      return;
    }

    signupSubmissionInFlightRef.current = true;
    setLoadingSignup(true);
    let createContactResponse: AxiosResponse | undefined;
    let submissionSucceeded = false;

    emitAnalytics('part_signup_submission_started', {
      flowType: 'classic',
      ...buildTelemetryBase(),
    });

    try {
      const commonContact = {
        firstName: userInfo?.contact.firstName,
        lastName: userInfo?.contact.lastName,
        phoneNumber: userInfo?.contact.phone,
        email: userInfo?.contact.email.toLowerCase().trim(),
        sendWelcomeEmail: true,
        preferedLanguage: getLanguageForApi(i18n.language),
        functions: [],
        referralSource: userInfo?.contact.howDidYouKnowUs,
        mailConsent: userInfo?.receiveRecommendation.email,
        smsConsent: userInfo?.receiveRecommendation.sms,
        status: true,
        language: getLanguageForApi(i18n.language),
      };

      if (userInfo?.contact?.profile === 'partCompany') {
        const company = {
          name: userInfo?.company.companyName,
          commercialName: userInfo?.company.companyName,
          siret: userInfo?.company?.siret,
          vat: userInfo?.company?.nbtva,
          language: getLanguageForApi(i18n.language),
          roles: 5,
          isCarProfessional: false,
          status: true,
          location: [],
        };

        const response = await PartsService.partCompanyCreate(company);

        const companyID = response.data.id;
        const contact = { ...commonContact, assignedEntityId: companyID, roles: 5 };

        createContactResponse = await PartsService.partContactCreate(contact);
      } else if (userInfo?.contact?.profile === 'partIndividual') {
        const contact = { ...commonContact, roles: setFirstAndThirdBits(5) };
        createContactResponse = await PartsService.partCustomerCreate(contact);
      }

      if (!createContactResponse) {
        throw new Error('Création du contact échouée');
      }

      localStorage.removeItem(STORAGE_KEY);
      submissionSucceeded = true;
      emitAnalytics('part_signup_submission_succeeded', {
        flowType: 'classic',
        ...buildTelemetryBase(),
      });
      return redirectToPartAfterSignup();
    } catch (error : any) {
      emitAnalytics('part_signup_submission_failed', {
        flowType: 'classic',
        failureStep: 'final_submit',
        ...buildTelemetryBase(),
      });
      const details = error.response.data.details as string []
      details.map ( detail => {
      toast.error(detail, toastConfig);
      })

      console.error(error);
    } finally {
      signupSubmissionInFlightRef.current = false;
      if (!submissionSucceeded) {
        // no-op: telemetry already emitted in catch
      }
      setLoadingSignup(false);
    }
  }, [buildTelemetryBase, i18n.language, redirectToPartAfterSignup, userInfo]);

  // ---------------------------------------------------------------------------
  // Validation panels (inchangé)
  // ---------------------------------------------------------------------------
  const validateFirstPanel = () => {
    const errors: string[] = [];
    if (!userInfo?.contact.firstName) errors.push(t('page2SignUp:first_name_required'));
    if (!userInfo?.contact.lastName) errors.push(t('page2SignUp:last_name_required'));
    if (!userInfo?.contact.phone) {
      errors.push(t('page2SignUp:phone_required'));
    } else if (userInfo?.contact.phone.length < 14) {
      errors.push(t('page2SignUp:phone_invalid'));
    }
    if (!userInfo?.contact.function) errors.push(t('page2SignUp:function_required'));

    if (errors.length > 0) {
      toast.warn(errors[0], toastConfig);
      setIsFirstPanelValidated(false);
    } else {
      setIsFirstPanelValidated(true);
      emitAnalytics('pro_signup_panel1_validated', {
        entryRoute: '/pro/signup',
        ...buildTelemetryBase(),
      });
      setOpenedPanel('company information');
    }
  };

  const validateSecondPanel = async () => {
    const errors: string[] = [];
    const isExistingCompanyFlow = existingCompanyMatch !== null;

    if (!isExistingCompanyFlow && !userInfo?.company.companyName) {
      errors.push(t('page2SignUp:company_name_required'));
    }
    if (!isExistingCompanyFlow && userInfo?.company.countryCode === 'FR') {
      if (!userInfo?.company.nbtva) errors.push(t('page2SignUp:nbtva_required'));
      if (!userInfo?.company.siret) errors.push(t('page2SignUp:siret_required'));
      if (!userInfo?.company.siren) errors.push(t('page2SignUp:siren_required'));
    }
    if (!isExistingCompanyFlow) {
      if (!userInfo?.company.address) errors.push(t('page2SignUp:address_required'));
      if (!userInfo?.company.postalCode) errors.push(t('page2SignUp:postal_code_required'));
      if (!userInfo?.company.city) errors.push(t('page2SignUp:city_required'));
    }

    if (errors.length > 0) {
      errors.forEach((error) => toast.warn(error, toastConfig));
      setIsSecondPanelValidated(false);
    } else {
      setIsSecondPanelValidated(true);
      if (isExistingCompanyFlow) {
        setOpenedPanel('documents');
        clearUploadedDocuments();
        return;
      }
      emitAnalytics('pro_signup_classic_company_information_validated', {
        countryCode: userInfo?.company?.countryCode,
        lookupOutcome: 'no_match',
        flowType: 'classic',
        ...buildTelemetryBase(),
      });
      emitAnalytics('pro_signup_documents_panel_opened', {
        countryCode: userInfo?.company?.countryCode,
        skipDocuments: false,
        flowType: 'classic',
        ...buildTelemetryBase(),
      });
      setOpenedPanel('documents');
    }
  };

  const validateThirdPanel = () => {
    if (shouldSkipDocumentUploadStep) {
      // emitAnalytics('pro_signup_company_rattachement_confirmed', {
      //   countryCode: userInfo?.company?.countryCode,
      //   flowType: 'company_rattachement',
      //   skipDocuments: true,
      //   ...buildTelemetryBase(),
      // });
      return
    }
    setIsThirdPanelValidated(true);
    subscribe();
  };

  const skipThirdPanel = () => {
    setOpenedPanel('completed');
    subscribe();
  };

  const subscribe = () => {
    if (!isFirstPanelValidated) {
      toast.warn(t('page2SignUp:fill_first_panel'), toastConfig);
    } else if (!isSecondPanelValidated) {
      toast.warn(t('page2SignUp:fill_second_panel'), toastConfig);
    } else {
      localStorage.removeItem(STORAGE_KEY);
      handleSignUpPro();
    }
  };

  const checkFirstPanelValidity = (): boolean => {
    return (
      userInfo?.contact?.firstName?.trim() !== '' &&
      userInfo?.contact?.lastName?.trim() !== '' &&
      userInfo?.contact?.phone?.trim() !== '' &&
      userInfo?.contact?.phone?.length >= 14 &&
      userInfo?.contact?.function?.trim() !== ''
    );
  };

  const checkSecondPanelValidity = (): boolean => {
    if (existingCompanyMatch) {
      return true;
    }

    return (
      userInfo?.company?.companyName?.trim() !== '' &&
      userInfo?.company?.address?.trim() !== '' &&
      userInfo?.company?.postalCode?.trim()?.length === 5 &&
      userInfo?.company?.city?.trim() !== '' &&
      (userInfo?.company?.countryCode !== 'FR' ||
        (userInfo?.company?.nbtva?.trim() !== '' &&
          userInfo?.company?.siret?.trim() !== '' &&
          userInfo?.company?.siren?.trim() !== ''))
    );
  };

  const checkThirdPanelValidity = (): boolean => {
    if (existingCompanyMatch) {
      return false;
    }

    return (
      userInfo?.documents.companyRegistration.length > 0 &&
      userInfo?.documents.identityCard.length > 0
    );
  };

  useEffect(() => {
    setIsFirstPanelValidated(checkFirstPanelValidity());
  }, [userInfo?.contact]);

  useEffect(() => {
    setIsSecondPanelValidated(checkSecondPanelValidity());
  }, [existingCompanyMatch, userInfo?.company]);

  useEffect(() => {
    setIsThirdPanelValidated(checkThirdPanelValidity());
  }, [userInfo?.documents]);

  return (
    <AuthContext.Provider
      value={{
        loadingSignin,
        loadingSignup,
        userInfo,
        loadUserInfoFromLocalStorage,
        openedPanel,
        isFirstPanelValidated,
        isSecondPanelValidated,
        isThirdPanelValidated,
        shouldSkipDocumentUploadStep,
        handleUserAuthentication,
        handleSignUpPro,
        handleSignUpPart,
        setUserInfo,
        setOpenedPanel,
        validateFirstPanel,
        validateSecondPanel,
        syncExistingCompanyMatchByVAT,
        activateExistingCompanyAttachment,
        validateThirdPanel,
        skipThirdPanel,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext doit être utilisé à l'intérieur de AuthProvider");
  }
  return context;
};
